import { useState } from 'react';
import { t } from '../i18n/es.js';

/**
 * Helper compartido — construye y muestra la URL pública del workspace del
 * tenant (customer SPA) con botones copy/open. Usado en TenantDetail,
 * TenantNew (post-creación) y Dashboard listado.
 *
 * En dev: https://{slug}.app.dev.zen.zervizdev.com/
 * Cambia automáticamente por env vía window.__ADMIN_CONFIG__ inyectado.
 */

/** Sufijo del dominio customer SPA según env. */
function customerDomainSuffix(): string {
  const cfg = (window as { __ADMIN_CONFIG__?: { envLabel?: string } }).__ADMIN_CONFIG__;
  const env = cfg?.envLabel ?? 'dev';
  return `app.${env}.zen.zervizdev.com`;
}

export function tenantWorkspaceUrl(slug: string): string {
  return `https://${slug}.${customerDomainSuffix()}/`;
}

interface TenantWorkspaceUrlProps {
  slug: string;
  /** Si true, no muestra label "URL del workspace", solo el value+actions inline. */
  compact?: boolean;
}

export function TenantWorkspaceUrl({ slug, compact = false }: TenantWorkspaceUrlProps): JSX.Element {
  const url = tenantWorkspaceUrl(slug);
  const [copied, setCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback silencioso si clipboard API bloqueada
    }
  }

  if (compact) {
    return (
      <span className="inline-flex items-center gap-2 text-xs">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-brand-600 hover:underline"
          data-testid="tenant-workspace-url"
        >
          {slug}.{customerDomainSuffix()}
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded border border-slate-300 px-2 py-0.5 text-[10px] text-slate-700 hover:bg-slate-50"
          aria-label={t.tenant.copyUrl}
          data-testid="tenant-workspace-copy"
        >
          {copied ? t.tenant.copied : '⧉'}
        </button>
      </span>
    );
  }

  return (
    <div
      className="rounded border border-brand-200 bg-brand-50 p-3"
      data-testid="tenant-workspace-card"
    >
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
        {t.tenant.workspaceUrl}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded bg-white px-2 py-1 font-mono text-sm text-slate-800">
          {url}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-xs hover:bg-slate-50"
          data-testid="tenant-workspace-copy"
        >
          {copied ? t.tenant.copied : t.tenant.copyUrl}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded bg-brand-500 px-3 py-1 text-xs text-white hover:bg-brand-600"
          data-testid="tenant-workspace-open"
        >
          {t.tenant.open} ↗
        </a>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">{t.tenant.workspaceUrlHelp}</p>
    </div>
  );
}
